import { useEffect, useState, type ReactNode } from "react";
import { CreditCard, Smartphone } from "lucide-react";
import { generateClient } from "aws-amplify/data";
import type { Schema } from "amplify/data/resource";

import { MoneyRepository } from "@/application/money/money_repository";
import { defaultTxFilter } from "@/application/money/types";

import { SidebarSection } from "./SidebarSection";

const client = generateClient<Schema>();

/** How far back to look for terminal use before calling it inactive. */
const TERMINAL_LOOKBACK = 200;

export interface PlatformStatus {
  connected: boolean;
  detail: string | null;
}

export interface PlatformsState {
  mobile: PlatformStatus | null;
  terminal: (PlatformStatus & { terminalName: string | null }) | null;
}

function relative(iso: string | null): string | null {
  if (!iso) return null;
  const days = Math.floor(
    (Date.now() - new Date(iso).getTime()) / (1000 * 60 * 60 * 24),
  );
  if (Number.isNaN(days)) return null;
  if (days <= 0) return "today";
  if (days === 1) return "yesterday";
  return `${days}d ago`;
}

/**
 * Which surfaces this merchant actually uses.
 *
 * Mobile comes from the device heartbeat — a merchant who has opened the app
 * has a snapshot. Terminal comes from a transaction carrying a `terminalId`,
 * which is written only by the Poynt handler: provisioning proves a terminal
 * exists, but a payment proves they are using it, and this row is about the
 * connection rather than the paperwork.
 */
export function PlatformsCard({
  businessId,
  onOpenMobile,
  onOpenTerminal,
}: {
  businessId: string;
  onOpenMobile: () => void;
  onOpenTerminal: () => void;
}) {
  const [state, setState] = useState<PlatformsState | null>(null);

  useEffect(() => {
    let cancelled = false;
    setState(null);

    async function load() {
      const [snapshot, transactions] = await Promise.allSettled([
        client.models.MerchantDeviceSnapshot.get({ businessId }),
        new MoneyRepository().listTransactions({
          businessId,
          filter: defaultTxFilter(),
          limit: TERMINAL_LOOKBACK,
        }),
      ]);

      if (cancelled) return;

      const device =
        snapshot.status === "fulfilled" ? snapshot.value.data : null;

      const terminalTx =
        transactions.status === "fulfilled"
          ? (transactions.value.items.find((tx) => tx.terminalId) ?? null)
          : null;

      setState({
        mobile: device?.lastSeenAt
          ? {
              connected: true,
              detail: [device.appVersion, relative(device.lastSeenAt)]
                .filter(Boolean)
                .join(" · "),
            }
          : { connected: false, detail: "No app activity" },
        terminal: terminalTx
          ? {
              connected: true,
              terminalName:
                terminalTx.terminalName ?? terminalTx.terminalId ?? null,
              detail: [
                terminalTx.terminalName ?? terminalTx.terminalId,
                terminalTx.created
                  ? relative(new Date(terminalTx.created * 1000).toISOString())
                  : null,
              ]
                .filter(Boolean)
                .join(" · "),
            }
          : {
              connected: false,
              terminalName: null,
              detail: "No terminal payments",
            },
      });
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [businessId]);

  return (
    <SidebarSection title="Platforms">
      <PlatformRow
        icon={<Smartphone className="text-subtle-foreground h-4 w-4" />}
        label="Mobile app"
        status={state?.mobile ?? null}
        onClick={onOpenMobile}
      />
      <PlatformRow
        icon={<CreditCard className="text-subtle-foreground h-4 w-4" />}
        label="Terminal"
        status={state?.terminal ?? null}
        onClick={onOpenTerminal}
      />
    </SidebarSection>
  );
}

function PlatformRow({
  icon,
  label,
  status,
  onClick,
}: {
  icon: ReactNode;
  label: string;
  status: PlatformStatus | null;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="hover:bg-muted -mx-2 flex w-full items-center gap-2.5 rounded-md px-2 py-1.5 text-left transition-colors focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:outline-none"
    >
      {icon}
      <span className="min-w-0 flex-1">
        <span className="text-foreground block text-sm">{label}</span>
        <span className="text-muted-foreground block truncate text-xs">
          {status ? (status.detail ?? "—") : "Checking…"}
        </span>
      </span>
      <span
        aria-label={
          status?.connected ? `${label} connected` : `${label} not connected`
        }
        className={`h-2 w-2 shrink-0 rounded-full ${
          status === null
            ? "bg-muted"
            : status.connected
              ? "bg-emerald-500"
              : "bg-muted-foreground/50"
        }`}
      />
    </button>
  );
}
